import { useMutation } from '@apollo/client'
import { CardActionArea, CardHeader, DialogActions, DialogContent, DialogTitle, useTheme } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import {
  Avatar,
  Button,
  Card,
  ContentBox,
  Dialog,
  Fields,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Text,
  useAlert,
  validations,
} from '..'
import { Business, CircleUser } from '../icons/icons'
import { COUNTRY_LIST, PROVINCES_LIST } from '../../constants'
import { mutations } from '../../graphql'

const DONOR_TYPE_OPTIONS = [
  { isOrganization: false, name: 'Personal', icon: CircleUser, testId: 'personal' },
  { isOrganization: true, name: 'Business / Organization', icon: Business, testId: 'business' },
]

/**
 * Logged-in "create an additional donor fund" dialog. Asks for the fields
 * createDonorFund accepts: portalName (required), phone (optional), address
 * (required), and isOrganization, which sets the new fund's identityKind to
 * business (using portalName as the organization name) instead of individual.
 */
export function CreateFundDialog({ open, onClose, me, onCreated }) {
  const theme = useTheme()
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [step, setStep] = useState('type')

  const { profile } = me ?? {}
  const fundProfile = me?.funds?.find((f) => !f.name?.startsWith('charity-') && !f.closed)?.fundProfile

  const defaultValues = useMemo(
    () => ({
      isOrganization: !!me?.isOrganization,
      // Always blank - this dialog creates a brand new fund, never prepopulated
      // from an existing one.
      portalName: '',
      phone: profile?.phone ?? '',
      address: {
        lineOne: fundProfile?.address?.lineOne ?? '',
        lineTwo: fundProfile?.address?.lineTwo ?? '',
        city: fundProfile?.address?.city ?? '',
        province: fundProfile?.address?.province ?? '',
        postalCode: fundProfile?.address?.postalCode ?? '',
        country: fundProfile?.address?.country ?? 'Canada',
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me]
  )

  const { register, handleSubmit, formState, getValues, setValue, watch, control, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  })
  const { isSubmitting, errors = {} } = formState

  useEffect(() => {
    if (open) {
      reset(defaultValues)
      setStep('type')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const [createDonorFund] = useMutation(mutations.funds.createDonorFund)

  const onSubmit = async ({ isOrganization, portalName, phone, address }) => {
    try {
      const { data } = await createDonorFund({
        variables: {
          input: {
            portalName,
            phone: phone || undefined,
            isOrganization,
            address,
          },
        },
      })

      onCreated(data.createDonorFund)
    } catch (err) {
      setAlert({
        message: err.message || 'Something went wrong creating your fund. Please try again.',
        severity: 'error',
      })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle sx={{ pb: 0 }}>Add Another Giving Fund</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {step === 'type' ? (
            <>
              <Text.Body sx={{ mb: 3 }}>Please select the type of giving fund you would like to create</Text.Body>
              <Stack spacing={2}>
                {DONOR_TYPE_OPTIONS.map(({ isOrganization, name, icon: Icon, testId }) => (
                  <Card
                    key={testId}
                    sx={{ width: '100%', height: 84, borderLeft: '4px solid', borderColor: 'primary.main' }}
                  >
                    <CardActionArea
                      data-testid={`createFundType-${testId}`}
                      onClick={() => {
                        setValue('isOrganization', isOrganization, { shouldValidate: true })
                        setStep('form')
                      }}
                      sx={{ height: '100%' }}
                    >
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: 'white', p: 1, width: 52, height: 52 }}>
                            <Icon style={{ fontSize: '28px', color: theme.palette.primary.main }} />
                          </Avatar>
                        }
                        title={name}
                        titleTypographyProps={{ variant: 'h6', color: 'primary', noWrap: true }}
                      />
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </>
          ) : (
            <>
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Text.Body>
                  Complete the information below to add this account to your existing GiveWise profile
                </Text.Body>
                <Text.Body>
                  All of your accounts will remain accessible using the same email address and password
                </Text.Body>
              </Stack>
              <ContentBox border>
                <FormControlLabel
                  size="small"
                  control={
                    <Switch
                      checked={!!watch('isOrganization')}
                      onChange={(e) => setValue('isOrganization', e.target.checked, { shouldValidate: true })}
                    />
                  }
                  label="Sign up as a Business or Organization"
                  sx={{ mb: 2 }}
                />
                <Stack alignItems="flex-start">
                  <Text.H6>Donor Details</Text.H6>
                  <Text.Body>
                    For compliance purposes, this name must match the source of funds and will appear on all tax
                    receipts.
                  </Text.Body>
                  <Fields.Text
                    inputProps={{ 'data-testid': 'portalName' }}
                    label="Donor Name *"
                    error={!!errors.portalName}
                    helperText={errors.portalName?.message}
                    {...register('portalName', { ...validations.required })}
                  />
                  <Fields.Text
                    inputProps={{ 'data-testid': 'phoneNumber' }}
                    type="text"
                    label="Phone Number"
                    error={!!errors.phone}
                    helperText={errors?.phone?.message}
                    {...register('phone', { ...validations.phoneNumber })}
                  />
                  <Text.Bold>Donor Address</Text.Bold>
                  <Text.Body>Address is used for tax receipts only. No physical mail will be sent to you.</Text.Body>
                  <Fields.Text
                    label="Address Line One"
                    required
                    error={!!errors.address?.lineOne}
                    helperText={errors?.address?.lineOne?.message}
                    {...register('address.lineOne', { ...validations.required })}
                  />
                  <Fields.Text
                    label="Address Line Two"
                    error={!!errors.address?.lineTwo}
                    helperText={errors?.address?.lineTwo?.message}
                    {...register('address.lineTwo')}
                  />
                  <Fields.Text
                    label="City"
                    required
                    error={!!errors.address?.city}
                    helperText={errors?.address?.city?.message}
                    {...register('address.city', { ...validations.required })}
                  />

                  {getValues('address.country') === 'Canada' ? (
                    <FormControl fullWidth>
                      <InputLabel id="createFundProvinceLabel">Province *</InputLabel>
                      <Controller
                        control={control}
                        name="address.province"
                        rules={{ ...validations.required }}
                        render={({ field: { ref, ...field } }) => (
                          <Select
                            {...field}
                            inputProps={{ 'data-testid': 'province', ref }}
                            label="Province *"
                            labelId="createFundProvinceLabel"
                            aria-label="Province *"
                            error={!!errors.address?.province}
                            helpertext={errors?.address?.province?.message}
                            required
                          >
                            {PROVINCES_LIST.map((p) => (
                              <MenuItem key={p} value={p}>
                                {p}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  ) : (
                    <Controller
                      control={control}
                      name="address.province"
                      rules={{ ...validations.required }}
                      render={({ field: { ref, ...field } }) => (
                        <Fields.Text
                          {...field}
                          inputProps={{ 'data-testid': 'province', ref }}
                          label="Province/State"
                          value={watch('address.province')}
                          required
                          error={!!errors.address?.province}
                          helperText={errors?.address?.province?.message}
                        />
                      )}
                    />
                  )}
                  <Fields.Text
                    label="Postal Code"
                    required
                    error={!!errors.address?.postalCode}
                    helperText={errors?.address?.postalCode?.message}
                    {...register('address.postalCode', { ...validations.required })}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="createFundCountryLabel">Country *</InputLabel>
                    <Controller
                      name="address.country"
                      control={control}
                      render={({ field: { ref, ...field } }) => (
                        <Select
                          {...field}
                          inputProps={{ 'data-testid': 'country', ref }}
                          onChange={(e) => {
                            field.onChange(e)
                            setValue('address.province', '', { shouldValidate: true })
                          }}
                          label="Country *"
                          labelId="createFundCountryLabel"
                          aria-label="Country *"
                          error={!!errors.address?.country}
                          helpertext={errors?.address?.country?.message}
                        >
                          {COUNTRY_LIST.map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>

                  <Alert {...alertProps} />
                </Stack>
              </ContentBox>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {step === 'form' && (
            <Button unstyled onClick={() => setStep('type')} data-testid="createFundBackButton">
              Back
            </Button>
          )}
          <Button unstyled onClick={onClose} data-testid="createFundCancelButton">
            Cancel
          </Button>
          {step === 'form' && (
            <Button type="submit" disabled={isSubmitting} data-testid="createFundSubmitButton">
              Create Fund
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}
